/** Imports */
import {z} from 'zod';
import {zUser} from '@/mongoSchemas/User/Users.zod';
import {zMedia} from '@/mongoSchemas/Post/Medias.zod';
import {zMongoObject, zMongoObjectId} from '@/types/utils';


/** Types */
export type PaymentPlanType = z.infer<typeof zPaymentPlan>;
export type PaymentPlanStatus = z.infer<typeof zOrganization.shape.planStatus>;
export type MemberRole = z.infer<typeof zMemberRole>;
export type Member = z.infer<typeof zOrganization.shape.members.element>;
export type Addon = z.infer<typeof zAddon>;
export type Organization = z.infer<typeof zOrganization>;
export type ClientAddon = z.infer<typeof zClientAddon>;
export type AiTokens = z.infer<typeof zAiTokens>;


/// Zod schemas
export const zMemberRole = z.enum([
    'owner',
    'approver',
    'user:canEdit',
    'user:canView',
    'pages:canEdit',
    'pages:canView',
    'posts:canEdit',
    'posts:canView',
    'reportings:canEdit',
    'reportings:canView',
    'foxlink:shortLinks:canEdit',
    'foxlink:shortLinks:canView',
    'foxlink:linkTrees:canEdit',
    'foxlink:linkTrees:canView',
]);
const zAddonId = z.enum([
    'amazon-aws-s3',
    'google-drive',
    'discord-bot',
    'short-links',
    'bulk-scheduler',
    'comments-central',
    'media-library',
]);
export const zPaymentPlan = z.enum([
    'NONE',
    'PROFESSIONAL',
    'TEAM',
    'ENTERPRISE',
]);
export const zClientAddon = z.object({
    id: zAddonId,
    label: z.string(),
    description: z.string(),
    image: z.string(),
    paymentPlan: z.union([
        zPaymentPlan,
        z.literal('ALL'),
    ]).array(),
    config: z.any().optional(),
    addedAt: z.instanceof(Date).optional(),
    updatedAt: z.instanceof(Date).optional(),
});
export const zAddon = z.object({
    id: zAddonId,
    config: z.any(),
    addedAt: z.instanceof(Date),
    updatedAt: z.instanceof(Date),
});
export const zAiTokens = z.object({
    usedTotal: z.number().optional().default(0),
    usedLast30Days: z.number().optional().default(0),
    lastResetAt: z.instanceof(Date).optional().default(() => new Date()),
    availableTokens: z.number().optional().default(-1),
});
export const zOrganization = zMongoObject.extend({

    // Meta
    name: z.string(),
    picture: zMedia.nullish(),
    creator: z.union([
        zMongoObjectId,
        zUser,
    ]),
    members: z.object({
        user: z.union([
            zMongoObjectId,
            zUser,
        ]),
        roles: zMemberRole.array(),
        invitedBy: z.union([
            zMongoObjectId,
            zUser,
        ]),
        invitedAt: z.instanceof(Date),
        inviteAccepted: z.boolean(),
        acceptedAt: z.instanceof(Date).optional(),
        inviteExpiresAt: z.instanceof(Date).optional(),
    }).array(),

    // Flags
    isAdminOrganization: z.boolean().default(false),
    deletedFlagSince: z.instanceof(Date).optional(),
    deletedFlagToken: z.instanceof(Date).optional(),

    // Subscription
    paymentPlan: zPaymentPlan,
    trialEndsAt: z.instanceof(Date).optional(),
    planStartedAt: z.instanceof(Date).optional(),
    stripeCustomerId: z.string().optional(),
    planStatus: z.enum([
        'TRIAL',
        'RUNNING',
        'CANCELED',
        'ENDED',
    ]).optional(),
    hasSpecialPlan: z.boolean().optional(),

    // Settings
    addons: zAddon.array(),
    allowedPages: z.string().array().optional(),

    // AI Tokens
    aiTokens: zAiTokens.optional(),

});
